import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorText } from "../../lib/api";

const EMPTY = { title: "", slug: "", excerpt: "", meta_description: "", cover: "/images/route.webp", content: "", published: true };
const COVERS = ["route", "impact", "vitrage", "camera-adas", "impact-parebrise", "adas", "optique", "optique-2", "hero-nuit", "conducteur", "domicile", "facade-haguenau", "accueil-haguenau", "atelier", "pret"];

function PostEditor({ post, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  useEffect(() => { setF(post ? { ...EMPTY, ...post } : EMPTY); }, [post]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { title: f.title, slug: f.slug || null, excerpt: f.excerpt, meta_description: f.meta_description, cover: f.cover, content: f.content, published: f.published };
    try {
      if (f.id) await api.put(`/admin/posts/${f.id}`, body);
      else await api.post("/admin/posts", body);
      toast.success("Article enregistré");
      onSaved();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <Dialog open={Boolean(post !== undefined)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="post-editor-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Modifier l'article" : "Nouvel article"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <input className="field" placeholder="Titre *" required value={f.title} onChange={set("title")} data-testid="post-title-input" />
          <input className="field" placeholder="Lien (slug) — généré automatiquement si vide" value={f.slug} onChange={set("slug")} data-testid="post-slug-input" />
          <textarea className="field" placeholder="Résumé *" required value={f.excerpt} onChange={set("excerpt")} data-testid="post-excerpt-input" />
          <input className="field" placeholder="Meta description (160 caractères max)" maxLength={170} value={f.meta_description} onChange={set("meta_description")} data-testid="post-meta-input" />
          <select className="field" value={f.cover} onChange={set("cover")} data-testid="post-cover-select">{COVERS.map((c) => <option key={c} value={`/images/${c}.webp`}>{c}</option>)}</select>
          <textarea className="field min-h-[260px] font-mono text-sm" placeholder={"Contenu *\n## Sous-titre\nParagraphe…\n- élément de liste"} required value={f.content} onChange={set("content")} data-testid="post-content-input" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={set("published")} data-testid="post-published-checkbox" />Publié</label>
          <button className="btn btn-blue" data-testid="post-save-button">Enregistrer</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BlogTab() {
  const [posts, setPosts] = useState([]);
  const [edit, setEdit] = useState(undefined);
  const load = () => api.get("/admin/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await api.delete(`/admin/posts/${id}`);
    load();
  };
  return (
    <div>
      <button onClick={() => setEdit(null)} className="btn btn-blue mb-4" data-testid="post-new-button"><Plus className="h-4 w-4" />Nouvel article</button>
      <div className="divide-y divide-slate-100 rounded-2xl bg-white ring-1 ring-slate-200" data-testid="posts-list">
        {posts.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-4 p-4">
            <div><p className="font-semibold text-ag-ink">{p.title}</p><p className="text-xs text-slate-500">/blog/{p.slug} · {p.published ? "Publié" : "Brouillon"}</p></div>
            <div className="flex shrink-0 gap-3">
              <button onClick={() => setEdit(p)} aria-label="Modifier" data-testid={`post-edit-${p.slug}`}><Pencil className="h-4 w-4 text-slate-500" /></button>
              <button onClick={() => del(p.id)} aria-label="Supprimer" data-testid={`post-delete-${p.slug}`}><Trash2 className="h-4 w-4 text-slate-500" /></button>
            </div>
          </div>
        ))}
      </div>
      <PostEditor post={edit} onClose={() => setEdit(undefined)} onSaved={() => { setEdit(undefined); load(); }} />
    </div>
  );
}

export function SettingsTab() {
  const [f, setF] = useState(null);
  const toNum = (v) => (v === "" || v == null ? null : Number(v));
  useEffect(() => { api.get("/settings").then((r) => setF(r.data || {})).catch(() => setF({})); }, []);
  if (!f) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/settings", { google_rating: toNum(f.google_rating), google_review_count: toNum(f.google_review_count), google_reviews_url: f.google_reviews_url || "", allogarage_rating: toNum(f.allogarage_rating), allogarage_review_count: toNum(f.allogarage_review_count), allogarage_url: f.allogarage_url || "" });
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <form onSubmit={save} className="max-w-xl space-y-3 rounded-2xl bg-white p-6 ring-1 ring-slate-200" data-testid="settings-form">
      <p className="text-sm text-slate-600">Sans clé API Google Places, la note et le nombre d'avis sont saisis ici. Laissez vide pour ne pas les afficher.</p>
      <label className="block text-sm font-semibold">Note Google (ex : 4.9)<input className="field mt-1" type="number" step="0.1" min="0" max="5" value={f.google_rating ?? ""} onChange={(e) => setF({ ...f, google_rating: e.target.value })} data-testid="settings-rating" /></label>
      <label className="block text-sm font-semibold">Nombre d'avis<input className="field mt-1" type="number" min="0" value={f.google_review_count ?? ""} onChange={(e) => setF({ ...f, google_review_count: e.target.value })} data-testid="settings-count" /></label>
      <label className="block text-sm font-semibold">Lien « Voir tous nos avis Google »<input className="field mt-1" value={f.google_reviews_url || ""} onChange={(e) => setF({ ...f, google_reviews_url: e.target.value })} data-testid="settings-url" /></label>
      <p className="pt-3 font-display text-lg font-bold text-ag-ink">Allogarage (avis vérifiés)</p>
      <label className="block text-sm font-semibold">Note Allogarage<input className="field mt-1" type="number" step="0.1" min="0" max="5" value={f.allogarage_rating ?? ""} onChange={(e) => setF({ ...f, allogarage_rating: e.target.value })} data-testid="settings-allo-rating" /></label>
      <label className="block text-sm font-semibold">Nombre d'avis vérifiés<input className="field mt-1" type="number" min="0" value={f.allogarage_review_count ?? ""} onChange={(e) => setF({ ...f, allogarage_review_count: e.target.value })} data-testid="settings-allo-count" /></label>
      <label className="block text-sm font-semibold">Lien Allogarage<input className="field mt-1" value={f.allogarage_url || ""} onChange={(e) => setF({ ...f, allogarage_url: e.target.value })} data-testid="settings-allo-url" /></label>
      <button className="btn btn-blue" data-testid="settings-save">Enregistrer</button>
    </form>
  );
}
